import React, { useState } from 'react';
import { PDFTemplate, PDFTemplateVisibility } from '../types';
import '../styles/PDFTemplateSettings.css';

interface PDFTemplateSettingsProps {
  templates: PDFTemplate[];
  onTemplatesChange: (templates: PDFTemplate[]) => void;
}

const DEFAULT_VISIBILITY: PDFTemplateVisibility = {
  showMaterials: true,
  showProcesses: true,
  showMaterialBreakdown: true,
  showMarkupDetails: true,
  showProcessCosts: true,
  showItemTotals: true,
  showSectionTotals: true,
  showUnitCosts: true,
};

const CLIENT_VISIBILITY: PDFTemplateVisibility = {
  showMaterials: true,
  showProcesses: false,
  showMaterialBreakdown: false,
  showMarkupDetails: false,
  showProcessCosts: false,
  showItemTotals: false,
  showSectionTotals: true,
  showUnitCosts: false,
};

export const PDFTemplateSettings: React.FC<PDFTemplateSettingsProps> = ({
  templates,
  onTemplatesChange,
}) => {
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  const addTemplate = (preset?: 'internal' | 'client') => {
    const id = `template_${Date.now()}`;
    const name = preset === 'client' ? 'Client View' : preset === 'internal' ? 'Internal View' : 'Custom Template';
    const visibility = preset === 'client' ? CLIENT_VISIBILITY : preset === 'internal' ? DEFAULT_VISIBILITY : DEFAULT_VISIBILITY;

    const newTemplate: PDFTemplate = {
      id,
      name,
      description: preset === 'client' ? 'For client distribution' : preset === 'internal' ? 'Full details for internal use' : 'Custom template',
      visibility,
      isDefault: templates.length === 0,
      createdAt: new Date(),
    };

    onTemplatesChange([...templates, newTemplate]);
    setExpandedTemplateId(id);
  };

  const deleteTemplate = (id: string) => {
    if (templates.length === 1) {
      alert('You must have at least one template');
      return;
    }
    onTemplatesChange(templates.filter((t) => t.id !== id));
    setExpandedTemplateId(null);
  };

  const updateTemplate = (id: string, updates: Partial<PDFTemplate>) => {
    onTemplatesChange(
      templates.map((t) => (t.id === id ? { ...t, ...updates } : t))
    );
  };

  const toggleVisibility = (templateId: string, key: keyof PDFTemplateVisibility) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    const newVisibility = {
      ...template.visibility,
      [key]: !template.visibility[key],
    };

    updateTemplate(templateId, { visibility: newVisibility });
  };

  const setDefaultTemplate = (id: string) => {
    onTemplatesChange(
      templates.map((t) => ({
        ...t,
        isDefault: t.id === id,
      }))
    );
  };

  return (
    <div className="pdf-template-settings">
      <div className="template-header">
        <h3>PDF Export Templates</h3>
        <p className="template-desc">Configure different PDF templates for client and internal views</p>
      </div>

      <div className="template-presets">
        <button
          onClick={() => addTemplate('internal')}
          className="preset-btn internal"
        >
          + Internal View
        </button>
        <button
          onClick={() => addTemplate('client')}
          className="preset-btn client"
        >
          + Client View
        </button>
        <button
          onClick={() => addTemplate()}
          className="preset-btn custom"
        >
          + Custom Template
        </button>
      </div>

      <div className="templates-list">
        {templates.map((template) => (
          <div key={template.id} className="template-card">
            <div className="template-card-header">
              <div className="template-info">
                <h4>{template.name}</h4>
                <p>{template.description}</p>
                {template.isDefault && <span className="default-badge">Default</span>}
              </div>
              <div className="template-actions">
                <button
                  onClick={() => setExpandedTemplateId(
                    expandedTemplateId === template.id ? null : template.id
                  )}
                  className="expand-btn"
                >
                  {expandedTemplateId === template.id ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {expandedTemplateId === template.id && (
              <div className="template-content">
                {editingTemplateId === template.id ? (
                  <div className="template-edit">
                    <div className="edit-field">
                      <label>Template Name</label>
                      <input
                        type="text"
                        value={template.name}
                        onChange={(e) =>
                          updateTemplate(template.id, { name: e.target.value })
                        }
                      />
                    </div>
                    <div className="edit-field">
                      <label>Description</label>
                      <textarea
                        value={template.description}
                        onChange={(e) =>
                          updateTemplate(template.id, { description: e.target.value })
                        }
                      />
                    </div>
                    <button
                      onClick={() => setEditingTemplateId(null)}
                      className="btn btn-primary"
                    >
                      Done Editing
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingTemplateId(template.id)}
                    className="btn btn-secondary"
                  >
                    Edit Details
                  </button>
                )}

                <div className="visibility-options">
                  <h5>Visibility Settings</h5>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showMaterials}
                        onChange={() => toggleVisibility(template.id, 'showMaterials')}
                      />
                      <span>Show Materials List</span>
                      <span className="help-text">Display all materials in the quote</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showProcesses}
                        onChange={() => toggleVisibility(template.id, 'showProcesses')}
                      />
                      <span>Show Processes/Modifications</span>
                      <span className="help-text">Display processes applied to materials</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showMaterialBreakdown}
                        onChange={() => toggleVisibility(template.id, 'showMaterialBreakdown')}
                      />
                      <span>Show Material Cost Breakdown</span>
                      <span className="help-text">Display detailed cost breakdown per material</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showUnitCosts}
                        onChange={() => toggleVisibility(template.id, 'showUnitCosts')}
                      />
                      <span>Show Unit Costs</span>
                      <span className="help-text">Display cost per unit</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showProcessCosts}
                        onChange={() => toggleVisibility(template.id, 'showProcessCosts')}
                      />
                      <span>Show Process Costs</span>
                      <span className="help-text">Display costs for individual processes</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showMarkupDetails}
                        onChange={() => toggleVisibility(template.id, 'showMarkupDetails')}
                      />
                      <span>Show Markup Details</span>
                      <span className="help-text">Display markup percentages</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showItemTotals}
                        onChange={() => toggleVisibility(template.id, 'showItemTotals')}
                      />
                      <span>Show Item Totals</span>
                      <span className="help-text">Display total cost per item</span>
                    </label>
                  </div>

                  <div className="visibility-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={template.visibility.showSectionTotals}
                        onChange={() => toggleVisibility(template.id, 'showSectionTotals')}
                      />
                      <span>Show Section Totals</span>
                      <span className="help-text">Display total cost per section</span>
                    </label>
                  </div>
                </div>

                <div className="template-footer">
                  {!template.isDefault && (
                    <button
                      onClick={() => setDefaultTemplate(template.id)}
                      className="btn btn-secondary"
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="btn btn-danger"
                  >
                    Delete Template
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
